import { writable, derived } from "svelte/store";
import { sortableList } from "@local/extensions/collections/sortable-list.js";
import { TRIOPS_URL as triopsUrl } from "#src/env";
import { APPS } from "#src/lib/framex/apps.js";

const deprecatedTriopsURLs = new Set(["https://triops.onrender.com"]);

const storedUi = JSON.parse(localStorage.getItem("ui")) || {};
const ui = {
  show_table: true,
  table_left: false,
  show_right_panel: true,
  show_left_panel: true,
  left_panel_mode: "framex",
  right_panel_tab: "properties",
  expand_right_panel: false,
  show_drafts: true,
  show_unsent: true,
  show_sent: true,
  use_wildcards_in_search: false,
  use_selected_in_search: false,
  show_searched_markers_by_default: false,
  use_selected_in_relabel: false,
  show_doc_files: true,
  show_doc_pages: true,
  show_doc_annotations: true,
  group_by: "type_id",
  group_by_direction: "asc",
  sort_by: "created_at",
  sort_by_direction: "asc",
  item_filter: null,
  draft_tally_request: null,
  current_document_id: null,
  document_view_show_items: true,
  document_view_show_collections: true,
  document_view_show_types: true,
  document_view_show_notes: true,
  document_view_show_dimensions: true,
  document_view_location_filter: null,
  document_view_item_filter: null,
  document_view_collection_filter: null,
  document_view_type_filter: null,
  document_view_note_filter: null,
  group_collections_by: "none",
  group_collections_by_direction: "asc",
  sort_collections_by: "created_at",
  sort_collections_by_direction: "asc",
  setting_display_show_unused_types: true,
  setting_display_show_spanning_dims: true,
  setting_display_tn_badges: true,
  setting_display_tn_qty: true,
  setting_display_tn_dims: true,
  setting_display_tn_mark: true,
  setting_display_edge_offsets: true,
  my_rfqs_only: true,

  // AI settings
  single_opening_llm_model: "openai-gpt-5.4",
  single_opening_reasoning_level: "low",
  page_analysis_llm_model: "openai-gpt-5.4",
  page_analysis_reasoning_level: "low",
  table_import_llm_model: "openai-gpt-5.4",
  table_import_reasoning_level: "low",
  note_prompt: "",
  note_llm_model: "openai-gpt-5.4",
  note_reasoning_level: "low",

  doc_measurement_scale: "1/4in:1ft",
  preserve_dimensions: false,
  setting_scroll_speed: 0,
  setting_triops_url: triopsUrl,
  setting_osd_initial_load: "defer",
  setting_osd_timeout: 30000,
  setting_osd_tile_retry_max: 1,
  setting_osd_tile_retry_delay: 2500,
  ...storedUi,
};

const validModels = {
  "claude-sonnet": true,
  "google-gemini-pro": true,
  "openai-gpt-4": true,
  "openai-gpt-5.2": true,
  "openai-gpt-5.4-mini": true,
  "openai-gpt-5.4": true,
};

[
  "single_opening_llm_model",
  "page_analysis_llm_model",
  "table_import_llm_model",
  "note_llm_model",
].forEach((key) => {
  if (!validModels[ui[key]]) {
    ui[key] = "openai-gpt-5.4";
  }
});

// One-time migration: leftPanelMode used to store "document" | "framex".
// Any persisted value that isn't a registered app id (e.g. the old
// "document" toggle state) falls back to FrameX, the default app.
if (ui.left_panel_mode !== null && !APPS.some((a) => a.id === ui.left_panel_mode)) {
  ui.left_panel_mode = "framex";
}

localStorage.setItem("ui", JSON.stringify(ui));

/**
 * @template T
 * @typedef {import('svelte/store').Writable<T>} Writable
 */

/**
 * @template T
 * @param {string} key
 * @param {T} value
 * @param {string} [lsKey="ui"]
 * @param {(current: T) => T} [reset]
 * @returns {Writable<T>}
 */
export function storedWritable(key, value, lsKey = "ui", reset = (x) => x) {
  const { set, update, ...rest } = writable(reset(value));

  return {
    ...rest,
    set: (v) => {
      set(v);
      ui[key] = v;
      localStorage.setItem(lsKey, JSON.stringify(ui));
    },
    update: (f) => {
      update((o) => {
        const v = f(o);
        ui[key] = v;
        localStorage.setItem(lsKey, JSON.stringify(ui));
      });
    },
  };
}

function createItemHt() {
  const { width } = window.screen;
  const defaultHt = width < 600 ? 110 : 200;
  const { subscribe, set, update } = writable(defaultHt);
  let fit = false;

  return {
    subscribe,
    zoomin: () => update((n) => Math.min(n * 1.3, 1012.5)),
    zoomout: () => update((n) => Math.max(n * (1 / 1.3), 85)),
    fit: (h) => {
      fit = true;
      if (h < 150) return set(110);
      const rows = width < 600 ? Math.floor(h / 110) : Math.floor(h / 143);
      return set(h / rows);
    },
    reset: () => set(143),
    hasBeenFit: () => fit,
  };
}

export function createSelected() {
  const { set, update, ...rest } = writable({});

  return {
    ...rest,
    set,
    update,
    select: (...ids) => {
      const idObj = ids.reduce((o, id) => {
        o[id] = true;
        return o;
      }, {});

      update((o) => {
        return { ...o, ...idObj };
      });
    },
    selectOnly: (...ids) => {
      const idObj = ids.reduce((o, id) => {
        o[id] = true;
        return o;
      }, {});

      set(idObj);
    },
    deselect: (...ids) => {
      update((o) => {
        ids.forEach((id) => {
          delete o[id];
        });
        return o;
      });
    },
    toggle: (...ids) => {
      update((o) => {
        const newO = { ...o };
        ids.forEach((id) => {
          if (newO[id]) {
            delete newO[id];
          } else {
            newO[id] = true;
          }
        });
        return newO;
      });
    },
  };
}

export function createSelectedObjects() {
  const { set, update, ...rest } = writable({});

  return {
    ...rest,
    set,
    update,
    select: (...objects) => {
      const idObj = objects.reduce((o, obj) => {
        o[obj.id] = obj;
        return o;
      }, {});

      update((o) => {
        return { ...o, ...idObj };
      });
    },
    selectOnly: (...objects) => {
      const idObj = objects.reduce((o, obj) => {
        o[obj.id] = obj;
        return o;
      }, {});

      set(idObj);
    },
    deselect: (...ids) => {
      update((o) => {
        ids.forEach((id) => {
          delete o[id];
        });
        return o;
      });
    },
  };
}

// Store route for each tab
export const navHistory = writable({
  items: null,
  collections: null,
  types: null,
  fabrications: null,
  documents: null,
});

export const selectedItems = createSelected(); // selected items in item list view
export const selectedCollections = createSelected(); // selected collections in collection list view
export const selectedTypes = createSelected(); // selected types in type list view
export const selectedFabs = createSelected(); // selected fabs in fab list view
export const selectedDocuments = createSelected(); // selected documents in document list view
export const selectedMaterials = createSelected(); // selected materials in material list view
export const selectedSurfaces = createSelected(); // selected surfaces in surface list view
export const selectedMakeups = createSelected(); // selected makeups in material list view
export const selectedItemProds = createSelected(); // selected item productss in item product list view
export const selectedCategories = createSelected(); // selected categories in category list view
export const selectedEdges = createSelected(); // selected edges in edge list view
export const selectedFabrications = createSelected(); // selected fabrications in fabrication list view
export const selectedProductSettings = createSelected(); // selected product settings in product settings list view
export const currentDocid = storedWritable(
  "current_document_id",
  ui.current_document_id,
);
export const currentSuppliers = writable(sortableList([])); // collection of current suppliers (for free users)
export const currentSupplierid = writable(null); // current supplier id (for free users)
export const makeupSupplierOptions = writable([]); // valid suppliers for type selection
export const makeupSupplier = writable(null); // current makeup supplier
export const selectedLocations = createSelected(); // selected locations in document view
export const selectedDimensions = createSelected(); // selected dimensions in document view
export const selectedRecords = createSelected(); // Selected items/types in document view sidebar
export const selectedAnnotations = createSelected(); // selected annotations in document view
export const hiddenLocations = createSelected(); // temporarily hidden locations in document view
export const selectedPages = createSelected(); // selected pages in document view
export const hiddenPages = createSelected(); // temporarily hidden pages in document view
export const itemOverlay = writable(null); // item to show in overlay in document view (null for no overlay)
export const itemOverlayMode = writable(null); // null | "item" | "collection"
export const selectedType = writable(null); // selected type (at most one can be selected) in item list view
export const pathRoot = writable("");
export const currentTool = writable("select");
export const itemHt = createItemHt();
export const detailVisible = writable(false); // for small screens: is the detail panel on the home screen visible?
export const showTable = storedWritable("show_table", ui.show_table);
export const showLeftPanel = storedWritable(
  "show_left_panel",
  ui.show_left_panel,
);
// Which app occupies the left-panel app slot in SHPX-ai: null (no app open)
// or an id from APPS (see src/lib/framex/apps.js). Ignored in the default build.
export const leftPanelMode = storedWritable("left_panel_mode", ui.left_panel_mode);
// Active tab in the FrameX right panel: "properties" | "pages". Ignored in
// the default build (see src/lib/framex/rightpanel/RightPanelTabs.svelte).
export const rightPanelTab = storedWritable("right_panel_tab", ui.right_panel_tab);
export const tableLeft = storedWritable("table_left", ui.table_left);
export const draftTallyRequest = storedWritable(
  "draft_tally_request",
  ui.draft_tally_request,
);
export const tempFeatureHole = writable(null);
export const tempFeatureEdgeFabrication = writable(null);
export const tempFeatureCornerFabrication = writable(null);
export const tempFeatureEdgeAlignedFab = writable(null);
export const docLocationTab = writable("item");
export const clipboard = writable(null);
export const showDocPages = storedWritable("show_doc_pages", ui.show_doc_pages);
export const showDocFiles = storedWritable("show_doc_files", ui.show_doc_files);
export const showDocAnnotations = storedWritable(
  "show_doc_annotations",
  ui.show_doc_annotations,
);
export const reviewing = writable(null);
export const showingTutorial = writable(false);
export const showSearchResults = writable(false);
export const WBPVersion = writable("1.1.0");
export const GSVersion = writable("1.1.0");

export const showDrafts = storedWritable("show_drafts", ui.show_drafts);
export const showUnsent = storedWritable("show_unsent", ui.show_unsent);
export const showSent = storedWritable("show_sent", ui.show_sent);
export const useWildcardsInSearch = storedWritable(
  "use_wildcards_in_search",
  ui.use_wildcards_in_search,
);
export const useSelectedInSearch = storedWritable(
  "use_selected_in_search",
  ui.use_selected_in_search,
);
export const showSearchedMarkersByDefault = storedWritable(
  "show_searched_markers_by_default",
  ui.show_searched_markers_by_default,
);
export const useSelectedInRelabel = storedWritable(
  "use_selected_in_relabel",
  ui.use_selected_in_relabel,
);
export const showRightPanel = storedWritable(
  "show_right_panel",
  ui.show_right_panel,
);
export const expandRightPanel = storedWritable(
  "expand_right_panel",
  ui.expand_right_panel,
);
export const groupBy = storedWritable("group_by", ui.group_by);
export const groupByDirection = storedWritable(
  "group_by_direction",
  ui.group_by_direction,
);
export const sortBy = storedWritable("sort_by", ui.sort_by);
export const sortByDirection = storedWritable(
  "sort_by_direction",
  ui.sort_by_direction,
);
export const documentViewShowItems = storedWritable(
  "document_view_show_items",
  ui.document_view_show_items,
);
export const documentViewShowCollections = storedWritable(
  "document_view_show_collections",
  ui.document_view_show_collections,
);
export const documentViewShowTypes = storedWritable(
  "document_view_show_types",
  ui.document_view_show_types,
);
export const documentViewShowNotes = storedWritable(
  "document_view_show_notes",
  ui.document_view_show_notes,
);
export const documentViewShowDimensions = storedWritable(
  "document_view_show_dimensions",
  ui.document_view_show_dimensions,
);
export const itemFilter = storedWritable("item_filter", ui.item_filter);
export const documentViewLocationFilter = storedWritable(
  "document_view_location_filter",
  ui.document_view_location_filter,
);
export const documentViewItemFilter = storedWritable(
  "document_view_item_filter",
  ui.document_view_item_filter,
);
export const documentViewCollectionFilter = storedWritable(
  "document_view_collection_filter",
  ui.document_view_collection_filter,
);
export const documentViewTypeFilter = storedWritable(
  "document_view_type_filter",
  ui.document_view_type_filter,
);
export const documentViewNoteFilter = storedWritable(
  "document_view_note_filter",
  ui.document_view_note_filter,
);
export const groupCollectionsBy = storedWritable(
  "group_collections_by",
  ui.group_collections_by,
);
export const groupCollectionsByDirection = storedWritable(
  "group_collections_by_direction",
  ui.group_collections_by_direction,
);
export const sortCollectionsBy = storedWritable(
  "sort_collections_by",
  ui.sort_collections_by,
);
export const sortCollectionsByDirection = storedWritable(
  "sort_collections_by_direction",
  ui.sort_collections_by_direction,
);
export const settingDisplayShowUnusedTypes = storedWritable(
  "setting_display_show_unused_types",
  ui.setting_display_show_unused_types,
);
export const settingDisplayShowSpanningDims = storedWritable(
  "setting_display_show_spanning_dims",
  ui.setting_display_show_spanning_dims,
);
export const settingDisplayTnBadges = storedWritable(
  "setting_display_tn_badges",
  ui.setting_display_tn_badges,
);
export const settingDisplayTnQty = storedWritable(
  "setting_display_tn_qty",
  ui.setting_display_tn_qty,
);
export const settingDisplayTnDims = storedWritable(
  "setting_display_tn_dims",
  ui.setting_display_tn_dims,
);
export const settingDisplayTnMark = storedWritable(
  "setting_display_tn_mark",
  ui.setting_display_tn_mark,
);
export const settingDisplayEdgeOffsets = storedWritable(
  "setting_display_edge_offsets",
  ui.setting_display_edge_offsets,
);
export const myRfqsOnly = storedWritable("my_rfqs_only", ui.my_rfqs_only);

export const singleOpeningLlmModel = storedWritable(
  "single_opening_llm_model",
  ui.single_opening_llm_model,
);
export const singleOpeningReasoningLevel = storedWritable(
  "single_opening_reasoning_level",
  ui.single_opening_reasoning_level,
);
export const pageAnalysisLlmModel = storedWritable(
  "page_analysis_llm_model",
  ui.page_analysis_llm_model,
);
export const pageAnalysisReasoningLevel = storedWritable(
  "page_analysis_reasoning_level",
  ui.page_analysis_reasoning_level,
);
export const tableImportLlmModel = storedWritable(
  "table_import_llm_model",
  ui.table_import_llm_model,
);
export const tableImportReasoningLevel = storedWritable(
  "table_import_reasoning_level",
  ui.table_import_reasoning_level,
);
export const notePrompt = storedWritable("note_prompt", ui.note_prompt);
export const noteReasoningLevel = storedWritable(
  "note_reasoning_level",
  ui.note_reasoning_level,
);
export const noteLlmModel = storedWritable("note_llm_model", ui.note_llm_model);

export const docMeasurementScale = storedWritable(
  "doc_measurement_scale",
  ui.doc_measurement_scale,
);
export const useDocMeasurementScale = writable(false);
export const preserveDimensions = storedWritable(
  "preserve_dimensions",
  ui.preserve_dimensions,
);
export const settingScrollSpeed = storedWritable(
  "setting_scroll_speed",
  ui.setting_scroll_speed,
);
export const settingTriopsUrl = storedWritable(
  "setting_triops_url",
  ui.setting_triops_url,
  "ui",
  (value) => (deprecatedTriopsURLs.has(value) ? triopsUrl : value),
);
export const settingOsdInitialLoad = storedWritable(
  "setting_osd_initial_load",
  ui.setting_osd_initial_load,
);
export const settingOsdTimeout = storedWritable(
  "setting_osd_timeout",
  ui.setting_osd_timeout,
);
export const settingOsdTileRetryMax = storedWritable(
  "setting_osd_tile_retry_max",
  ui.setting_osd_tile_retry_max,
);
export const settingOsdTileRetryDelay = storedWritable(
  "setting_osd_tile_retry_delay",
  ui.setting_osd_tile_retry_delay,
);

// Represent all of the settings that require update of item list
// as a unique key
export const refreshItemList = derived(
  [
    settingDisplayTnBadges,
    settingDisplayTnDims,
    settingDisplayTnMark,
    settingDisplayTnQty,
    settingDisplayEdgeOffsets,
  ],
  (bools) => {
    return bools.map((b) => (b ? 1 : 0)).join("");
  },
);
